import React, { useState, useMemo } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Chip, OutlinedInput, InputAdornment, Checkbox, ListItemText, Radio } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search';

const ACCENT_BLUE = 'rgb(34, 93, 153)';

const selectSx = {
  borderRadius: '10px',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 93, 153, 0.15)' },
}

const chipSx = {
  backgroundColor: 'rgba(34, 93, 153, 0.08)',
  color: ACCENT_BLUE,
  fontWeight: 600,
  fontSize: 12,
  height: 24,
}

const SearchBox = ({ value, onChange, placeholder }) => (
  <Box sx={{ px: 1.5, py: 1, position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
    <TextField
      size="small"
      fullWidth
      autoFocus
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onKeyDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      InputProps={{
        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
      }}
    />
  </Box>
)

const SelectAllRow = ({ checked, indeterminate, onChange }) => (
  <MenuItem onClick={(e) => { e.stopPropagation(); onChange() }} sx={{ borderBottom: '1px solid rgba(34, 93, 153, 0.08)' }}>
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      size="small"
      sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: ACCENT_BLUE } }}
    />
    <ListItemText primary="Select all" primaryTypographyProps={{ sx: { fontWeight: 700, fontSize: 13.5 } }} />
  </MenuItem>
)

function DropDown({
  label, minWidth = 200, fullWidth = false, options, getKey, getLabel, getChipLabel, getGroupLabel,
  selectedKeys, onChange, allLabel,
  multiple = true,
  required = false,
}) {
  const [search, setSearch] = useState('')
  const chipLabelFn = getChipLabel || getLabel

  const filtered = useMemo(
    () => options.filter((o) => getLabel(o).toLowerCase().includes(search.toLowerCase())),
    [options, search, getLabel]
  )

  const grouped = useMemo(() => {
    if (!getGroupLabel) return null
    const groups = {}
    filtered.forEach((o) => {
      const g = getGroupLabel(o)
      if (!groups[g]) groups[g] = []
      groups[g].push(o)
    })
    return Object.entries(groups).map(([groupLabel, rows]) => ({ groupLabel, rows }))
  }, [filtered, getGroupLabel])

  const widthSx = fullWidth ? { width: '100%' } : { minWidth };

  // ---- single-select mode ----
  if (!multiple) {
    const selectedKey = selectedKeys
    const selectedOption = options.find((o) => getKey(o) === selectedKey)

    const pickOne = (opt) => {
      onChange(opt)
      setSearch('')
    }

    const renderSingleItem = (opt) => {
      const key = getKey(opt)
      return (
        <MenuItem key={key} onClick={() => pickOne(opt)} selected={key === selectedKey}>
          <Radio checked={key === selectedKey} size="small" sx={{ '&.Mui-checked': { color: ACCENT_BLUE } }} />
          <ListItemText primary={getLabel(opt)} />
        </MenuItem>
      )
    }

    return (
      <FormControl size="small" sx={widthSx} required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={selectedKey || ''}
          onChange={() => {}}
          onClose={() => setSearch('')}
          renderValue={() => selectedOption ? getLabel(selectedOption) : ''}
          MenuProps={{ PaperProps: { sx: { maxHeight: 420 } }, autoFocus: false }}
          sx={selectSx}
        >
          <SearchBox value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${label.toLowerCase()}...`} />
          {filtered.length === 0 && (
            <MenuItem disabled sx={{ fontSize: 13, color: 'rgb(170,170,180)' }}>No matches</MenuItem>
          )}
          {grouped
            ? grouped.flatMap((group) => [
                <MenuItem key={`hdr-${group.groupLabel}`} disabled sx={{ fontWeight: 700, fontSize: 12, color: ACCENT_BLUE, backgroundColor: 'rgba(34, 93, 153, 0.04)', opacity: '1 !important' }}>
                  {group.groupLabel}
                </MenuItem>,
                ...group.rows.map(renderSingleItem),
              ])
            : filtered.map(renderSingleItem)}
        </Select>
      </FormControl>
    )
  }

  // ---- multi-select mode (unchanged, original behaviour) ----
  const allVisibleSelected = filtered.length > 0 && filtered.every((o) => selectedKeys.includes(getKey(o)))
  const someVisibleSelected = filtered.some((o) => selectedKeys.includes(getKey(o)))

  const toggleSelectAll = () => {
    const visibleKeys = filtered.map(getKey)
    const next = allVisibleSelected
      ? selectedKeys.filter((k) => !visibleKeys.includes(k))
      : [...new Set([...selectedKeys, ...visibleKeys])]
    onChange(next)
  }

  const toggleOne = (key) => {
    const next = selectedKeys.includes(key)
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key]
    onChange(next)
  }

  const renderItem = (opt) => {
    const key = getKey(opt)
    return (
      <MenuItem key={key} onClick={() => toggleOne(key)}>
        <Checkbox checked={selectedKeys.includes(key)} size="small" sx={{ '&.Mui-checked': { color: ACCENT_BLUE } }} />
        <ListItemText primary={getLabel(opt)} />
      </MenuItem>
    )
  }

  return (
    <FormControl size="small" sx={widthSx}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={selectedKeys}
        onChange={() => {}}
        onClose={() => setSearch('')}
        input={<OutlinedInput label={label} />}
        renderValue={(sel) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {sel.length === options.length
              ? <Chip label={allLabel || `All ${label.toLowerCase()}`} size="small" sx={chipSx} />
              : sel.slice(0, 2).map((key) => {
                  const opt = options.find((o) => getKey(o) === key)
                  return <Chip key={key} label={opt ? chipLabelFn(opt) : key} size="small" sx={chipSx} />
                })
            }
            {sel.length > 2 && sel.length !== options.length && (
              <Chip label={`+${sel.length - 2}`} size="small" sx={chipSx} />
            )}
          </Box>
        )}
        MenuProps={{ PaperProps: { sx: { maxHeight: 420 } }, autoFocus: false }}
        sx={selectSx}
      >
        <SearchBox value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${label.toLowerCase()}...`} />
        <SelectAllRow checked={allVisibleSelected} indeterminate={someVisibleSelected && !allVisibleSelected} onChange={toggleSelectAll} />
        {filtered.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 13, color: 'rgb(170,170,180)' }}>No matches</MenuItem>
        )}
        {grouped
          ? grouped.flatMap((group) => [
              <MenuItem key={`hdr-${group.groupLabel}`} disabled sx={{ fontWeight: 700, fontSize: 12, color: ACCENT_BLUE, backgroundColor: 'rgba(34, 93, 153, 0.04)', opacity: '1 !important' }}>
                {group.groupLabel}
              </MenuItem>,
              ...group.rows.map(renderItem),
            ])
          : filtered.map(renderItem)}
      </Select>
    </FormControl>
  )
}

export default DropDown